import * as React from "react";
import styled from "@emotion/styled";
import { useForm, useFieldArray } from "react-hook-form";
import { Table } from "atoms";
import {
  useUpdateProductVariants,
  type UpdateProductVariantsReq,
  type VariantField,
} from "../../api/useUpdateProductVariants";
import type { StepProps, TemplateVariant } from "../../types";
import { ProductWizardStep } from "../ProductWizardStep";
import { VariantRow } from "./StepPricing";

/** String for RegExp constructor that matches any framed variant color */
const FRAME_REGEX = "FRAME-(BLACK|WHITE|NATURAL)$";

/** Sort variants with unframed first, framed last */
const sortVariants = (a: VariantField, b: VariantField) => {
  const aIsFramed = !a.variantId?.endsWith("FRAME-NONE");
  const bIsFramed = !b.variantId?.endsWith("FRAME-NONE");
  if (!aIsFramed && bIsFramed) {
    return -1;
  }
  if (aIsFramed && !bIsFramed) {
    return 1;
  }
  return 0;
};

function StepArtPrintPricing({ state, dispatch, moveBack }: StepProps) {
  const { isLoading, mutate } = useUpdateProductVariants(state.product.id);

  // Use the product's template data to create a map of each variant by variant id which we'll use
  // when rendering each row of the pricing table to display minimum and recommended prices.
  const variantMap = React.useMemo(
    () =>
      state.productTemplate.variants.reduce<Record<string, TemplateVariant>>(
        (acc, cur) => {
          acc[cur.id] = cur;
          return acc;
        },
        {},
      ),
    [state.productTemplate],
  );

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
  } = useForm<UpdateProductVariantsReq>({
    defaultValues: {
      variants: state.product.variants
        .reduce<VariantField[]>((acc, cur) => {
          // Use the variant's templateVariantId to see if it's framed or not
          const isFramed = new RegExp(FRAME_REGEX).test(
            cur.templateVariantId ?? "",
          );
          // Then extract the dimensions from it, something like "8X12"
          const size = /\d+X\d+/.exec(cur.templateVariantId ?? "")?.[0];
          // Create a new regex that matches any other framed variant of the same size, regardless of color
          const sizeMatcher = new RegExp(`${size}-${FRAME_REGEX}`);

          if (
            isFramed &&
            acc.find((v) => sizeMatcher.test(v.variantId ?? ""))
          ) {
            // We already have a framed variant with that size in another color, no need to add it
            return acc;
          }

          // This is either an unframed variant or a framed variant in a new size, so convert the variant
          // object to the shape needed for form fields and update hook and append to accumulator
          return [
            ...acc,
            {
              ...variantMap[cur.templateVariantId ?? ""],
              id: cur.id,
              variantId: cur.templateVariantId,
              priceInCents: Number(cur.price.amount) * 100,
              title: `${isFramed ? "Framed" : "Unframed"} ${size?.toLowerCase()}`,
            },
          ];
        }, [])
        .sort(sortVariants),
    },
  });

  const { fields } = useFieldArray({ control, name: "variants" });

  const moveNext = () => dispatch({ type: "moveNext" });
  const onSubmit = handleSubmit((formData) => {
    if (isDirty) {
      mutate(formData, {
        onSuccess: moveNext,
      });
    } else {
      moveNext();
    }
  });

  return (
    <ProductWizardStep
      stepTitle="Pricing"
      state={state}
      moveBack={moveBack}
      moveBackDisabled={isDirty}
      nextStep={onSubmit}
      nextStepDisabled={isLoading}
      isForm
    >
      <STable>
        <thead>
          <tr>
            <td>Variant</td>
            <td>Min. Price</td>
            <td>List Price</td>
            <td>You Make</td>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, index) => (
            <VariantRow
              key={field.id}
              {...field}
              index={index}
              error={errors.variants?.[index]?.priceInCents?.message}
              control={control}
              hasVariants
            />
          ))}
        </tbody>
      </STable>
    </ProductWizardStep>
  );
}

StepArtPrintPricing.displayName = "Pricing";

const STable = styled(Table)(({ theme }) => ({
  width: "max-content",
  td: {
    fontSize: 15,
    verticalAlign: "center",
    minWidth: "calc(100% / 4)",
    width: "max-content",
  },
  "thead td": { fontSize: 16, fontWeight: "bold", color: theme.colors.fg },
}));

export { StepArtPrintPricing };
